import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Category, type Group, type PendingReading, type Summary } from '../api/client';
import { useSession } from '../session/SessionContext';
import { Ficha } from '../components/Ficha';
import { BarrasComparativas } from '../components/BarrasComparativas';
import { AvisoError } from '../components/Aviso';
import { Link } from 'react-router-dom';
import { duracionLegible } from '../formato';
import { GraficaTemporal, type PuntoTemporal } from '../components/GraficaTemporal';
import i18n from '../i18n';
import { claveVisible } from '../documentos/clave';

export function Panel({ grupos }: { grupos: Group[] }) {
  const { t, i18n: traductor } = useTranslation();
  const idioma = traductor.language;
  const { estado, puede } = useSession();
  const [resumen, setResumen] = useState<Summary | null>(null);
  const [serie, setSerie] = useState<PuntoTemporal[]>([]);
  const [categorias, setCategorias] = useState<Category[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [pendientes, setPendientes] = useState<PendingReading[]>([]);
  /** Nombre de cada permiso en el idioma de la persona. */
  const [nombresDePermiso, setNombresDePermiso] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!puede('dashboard.view')) return;
    api.summary().then(setResumen).catch(setError);
    api.categories().then(setCategorias).catch(setError);
  }, [puede]);

  useEffect(() => {
    if (!puede('audit.read')) return;
    api
      .accessTimeline()
      .then((dias) =>
        setSerie(dias.map((dia) => ({ dia: dia.day, valores: [dia.sessions, dia.readers] }))),
      )
      .catch(setError);
  }, [puede]);

  /**
   * Las lecturas obligatorias propias. No lleva permiso: todo el mundo tiene
   * las suyas, y son lo primero que debe ver quien entra — antes que cualquier
   * indicador, porque es lo único de esta pantalla que le pide algo.
   *
   * Un fallo aquí no pinta un aviso rojo en el panel de todos: si el módulo no
   * contesta, se calla y el resto del tablero sigue sirviendo.
   */
  useEffect(() => {
    api.pendingReadings().then(setPendientes).catch(() => setPendientes([]));
  }, []);

  const sesion = estado.status === 'autenticado' ? estado.session : null;
  const fecha = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  const administra = sesion?.administers ?? false;

  // Sólo a quien administra: a nadie más le dice nada útil, y a quien sólo
  // consulta le enseñaba una lista de códigos técnicos en inglés.
  useEffect(() => {
    if (!administra) return;
    api
      .permissions()
      .then((lista) => setNombresDePermiso(new Map(lista.map((p) => [p.code, p.name]))))
      .catch(() => setNombresDePermiso(new Map()));
  }, [administra, idioma]);

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('dashboard.title')}</h1>
        <p className="pagina__bajada">{t('dashboard.subtitle')}</p>
      </div>

      <AvisoError error={error} />

      {pendientes.length > 0 && (
        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('reading.myPending')}</h2>
            <span
              className={`pildora pildora--${
                pendientes.some((p) => p.daysLeft < 0) ? 'critico' : 'aviso'
              }`}
            >
              <span className="pildora__punto" aria-hidden="true" />
              {pendientes.length}
            </span>
          </div>
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('documents.name')}</th>
                  <th>{t('reading.dueOn')}</th>
                  <th>{t('reading.status')}</th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((lectura) => (
                  <tr key={lectura.documentId}>
                    <td>
                      {/* Va directo al visor, no a la ficha: lo que hay que
                          hacer es leerlo, y un salto de más entre el aviso y la
                          tarea es un salto en el que la gente se pierde. */}
                      <Link to={`/documentos/${lectura.documentId}/leer`}>{lectura.title}</Link>
                      <div className="campo__ayuda mono">{claveVisible(lectura)}</div>
                    </td>
                    <td className="mono">{fecha.format(new Date(lectura.dueOn))}</td>
                    <td>
                      {lectura.daysLeft < 0 ? (
                        <span className="pildora pildora--critico">
                          <span className="pildora__punto" aria-hidden="true" />
                          {t('reading.overdueBy', { days: Math.abs(lectura.daysLeft) })}
                        </span>
                      ) : lectura.daysLeft === 0 ? (
                        <span className="pildora pildora--aviso">
                          <span className="pildora__punto" aria-hidden="true" />
                          {t('reading.dueToday')}
                        </span>
                      ) : (
                        <span className="dato__valor">
                          {t('reading.dueIn', { days: lectura.daysLeft })}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {resumen && (
        <section className="fichas" aria-label={t('dashboard.indicators')}>
          <Ficha
            etiqueta={t('dashboard.publishedDocuments')}
            valor={resumen.publishedDocuments}
            nota={t('dashboard.currentDocuments')}
          />
          <Ficha
            etiqueta={t('dashboard.expiringSoon')}
            valor={resumen.expiringSoon}
            nota={t('dashboard.next30days')}
            tono={resumen.expiringSoon > 0 ? 'atencion' : 'pendiente'}
          />
          <Ficha etiqueta={t('dashboard.activeUsers')} valor={resumen.activeUsers} />
          <Ficha
            etiqueta={t('dashboard.pendingInvitations')}
            valor={resumen.pendingInvitations}
            nota={resumen.pendingInvitations > 0 ? t('dashboard.awaitingPassword') : undefined}
            tono={resumen.pendingInvitations > 0 ? 'atencion' : 'normal'}
          />
          <Ficha etiqueta={t('dashboard.groups')} valor={resumen.groups} />
          <Ficha etiqueta={t('dashboard.categories')} valor={resumen.categories} />
        </section>
      )}

      {/* Un proceso que no corrió deja normativos vencidos figurando como
          vigentes. Es lo primero que alguien debe ver si pasa. */}
      {resumen?.expirationJob.delayed && (
        <p className="aviso aviso--atencion">
          {resumen.expirationJob.lastSuccessAt
            ? t('dashboard.jobDelayed', {
                fecha: new Date(resumen.expirationJob.lastSuccessAt).toLocaleString(),
                horas: resumen.expirationJob.toleratedHours,
              })
            : t('dashboard.jobNeverRan')}
        </p>
      )}

      {resumen && puede('audit.read') && (
        <div className="rejilla">
          <section className="tarjeta">
            <div className="tarjeta__encabezado">
              <h2 className="tarjeta__titulo">{t('dashboard.readingTitle')}</h2>
              <span className="tarjeta__nota">
                {t('dashboard.lastDays', { days: resumen.reading.windowDays })}
              </span>
              <Link className="boton boton--discreto" to="/consultas">
                {t('dashboard.seeAccessLog')}
              </Link>
            </div>
            <div className="datos">
              <div className="dato">
                <span className="dato__etiqueta">{t('dashboard.readingSessions')}</span>
                <span className="dato__valor dato__valor--mono">{resumen.reading.sessions}</span>
              </div>
              <div className="dato">
                <span className="dato__etiqueta">{t('dashboard.readers')}</span>
                <span className="dato__valor dato__valor--mono">{resumen.reading.readers}</span>
              </div>
              <div className="dato">
                <span className="dato__etiqueta">{t('dashboard.readingTime')}</span>
                <span className="dato__valor dato__valor--mono">
                  {duracionLegible(resumen.reading.totalSeconds)}
                </span>
              </div>
              <div className="dato">
                <span className="dato__etiqueta">{t('dashboard.documentsRead')}</span>
                <span className="dato__valor dato__valor--mono">
                  {resumen.reading.documentsRead}
                </span>
              </div>
            </div>
            <p className="tarjeta__pie">{t('dashboard.readingNote')}</p>
          </section>

          <section className="tarjeta">
            <div className="tarjeta__encabezado">
              <h2 className="tarjeta__titulo">{t('dashboard.mostRead')}</h2>
            </div>
            {resumen.reading.mostRead.length === 0 ? (
              <p className="vacio">{t('dashboard.noReadings')}</p>
            ) : (
              <BarrasComparativas
                unidad={t('dashboard.consultations')}
                datos={resumen.reading.mostRead.map((documento) => ({
                  clave: documento.documentId,
                  etiqueta: claveVisible(documento),
                  valor: documento.sessions,
                }))}
              />
            )}
            <p className="tarjeta__pie">{t('dashboard.mostReadNote')}</p>
          </section>
        </div>
      )}

      {puede('audit.read') && (
        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('charts.readingOverTime')}</h2>
            <span className="tarjeta__nota">{t('dashboard.lastDays', { days: 30 })}</span>
          </div>
          <GraficaTemporal
            puntos={serie}
            series={[
              { nombre: t('charts.readings'), color: 'var(--serie-1)' },
              { nombre: t('charts.readers'), color: 'var(--serie-2)' },
            ]}
          />
          <p className="tarjeta__pie">{t('charts.readingOverTimeNote')}</p>
        </section>
      )}

      <div className="rejilla">
        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('charts.byCategory')}</h2>
            <span className="tarjeta__nota">{t('catalog.documents', { count: 0 }).replace(/^\d+\s*/, '')}</span>
          </div>
          <BarrasComparativas
            unidad={t('catalog.documents', { count: 0 }).replace(/^\d+\s*/, '')}
            datos={categorias
              .filter((categoria) => categoria.documentCount > 0)
              .sort((a, b) => b.documentCount - a.documentCount)
              .map((categoria) => ({
                clave: categoria.id,
                etiqueta: categoria.name,
                valor: categoria.documentCount,
              }))}
          />
          <p className="tarjeta__pie">{t('charts.byCategoryNote')}</p>
        </section>

        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('dashboard.membersByGroup')}</h2>
            <span className="tarjeta__nota">{t('dashboard.people')}</span>
          </div>
          <BarrasComparativas
            unidad={t('dashboard.people')}
            datos={[...grupos]
              // Su alcance: la gente de toda la rama, que es quien vería lo que se le asigne.
              .sort((a, b) => b.branchMemberCount - a.branchMemberCount)
              .map((grupo) => ({ clave: grupo.id, etiqueta: grupo.name, valor: grupo.branchMemberCount }))}
          />
          <p className="tarjeta__pie">{t('dashboard.membersByGroupNote')}</p>
        </section>

        {/* Lo que puede hacer, en palabras y sólo para quien administra. Antes lo
            veía todo el mundo y en códigos —`audit.read`, `dashboard.view`—, que
            no le dicen nada a quien no los programó. */}
        {administra && sesion && (
          <section className="tarjeta">
            <div className="tarjeta__encabezado">
              <h2 className="tarjeta__titulo">{t('session.canDo')}</h2>
              <span className="tarjeta__nota">{sesion.permissions.length}</span>
            </div>
            {sesion.permissions.length > 0 ? (
              <div className="nube">
                {[...sesion.permissions]
                  .map((permiso) => ({ permiso, nombre: nombresDePermiso.get(permiso) ?? permiso }))
                  .sort((a, b) => a.nombre.localeCompare(b.nombre, i18n.language))
                  .map(({ permiso, nombre }) => (
                    <span key={permiso} className="pildora pildora--permiso" title={permiso}>
                      {nombre}
                    </span>
                  ))}
              </div>
            ) : (
              <p className="vacio">{t('session.noPermissions')}</p>
            )}
            <p className="tarjeta__pie">{t('session.groupsExplanation')}</p>
          </section>
        )}
      </div>
    </>
  );
}
